import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Locale, Site, SiteGroups } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyState, LinkButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { SettingsCards } from '../../components/SettingsCards';
import { useToastOnSuccess } from '../../components/Toast';
import { GroupList, section, timeZoneNames, useScopedSettings } from './api';
import { KioskLookTab } from './KioskLook';
import { Tabs, useTab } from './Tabs';

function toForm(s: Site) {
  return {
    name: s.name,
    line1: s.address.line1,
    line2: s.address.line2,
    town: s.address.town,
    county: s.address.county,
    postcode: s.address.postcode,
    timeZone: s.timeZone,
    language: s.language,
    replyToName: s.replyToName,
    replyToEmail: s.replyToEmail,
  };
}

function DetailsTab({ site, canEdit }: { site: Site; canEdit: boolean }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => toForm(site));
  useEffect(() => setForm(toForm(site)), [site]);
  const refresh = async (s: Site) => {
    qc.setQueryData(['sites', 'detail', site.id], s);
    await qc.invalidateQueries({ queryKey: ['sites'] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/sites/${site.id}`, {
        body: {
          name: form.name,
          address: { line1: form.line1, line2: form.line2, town: form.town, county: form.county, postcode: form.postcode },
          timeZone: form.timeZone,
          language: form.language,
          replyToName: form.replyToName,
          replyToEmail: form.replyToEmail,
        },
        schema: Site,
      }),
    onSuccess: refresh,
  });
  const status = useMutation({
    mutationFn: (next: 'active' | 'archived') => api.patch(`/sites/${site.id}`, { body: { status: next }, schema: Site }),
    onSuccess: refresh,
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const errs = fieldErrors(save.error);
  const changed = JSON.stringify(form) !== JSON.stringify(toForm(site));
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <div className="space-y-6">
      <Card
        footer={
          canEdit ? (
            <Button type="submit" form="site-details" variant="primary" disabled={!changed || save.isPending}>
              {t('common.save')}
            </Button>
          ) : null
        }
      >
        <form id="site-details" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <ErrorLine error={save.error} />
          </div>
          <TextField label={t('sitesUi.field.name')} required maxLength={80} value={form.name} onChange={set('name')} disabled={!canEdit} error={errs['name']} className="sm:col-span-2" />
          <TextField label={t('sitesUi.field.line1')} value={form.line1} onChange={set('line1')} disabled={!canEdit} />
          <TextField label={t('sitesUi.field.line2')} value={form.line2} onChange={set('line2')} disabled={!canEdit} />
          <TextField label={t('sitesUi.field.town')} value={form.town} onChange={set('town')} disabled={!canEdit} />
          <TextField label={t('sitesUi.field.county')} value={form.county} onChange={set('county')} disabled={!canEdit} />
          <TextField label={t('sitesUi.field.postcode')} value={form.postcode} onChange={set('postcode')} disabled={!canEdit} />
          <SelectField label={t('sitesUi.field.timeZone')} hint={t('sitesUi.field.timeZoneHint')} value={form.timeZone} onChange={set('timeZone')} disabled={!canEdit} error={errs['timeZone']}>
            {timeZoneNames(form.timeZone).map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('sitesUi.field.language')} value={form.language} onChange={set('language')} disabled={!canEdit}>
            {Locale.options.map((l) => (
              <option key={l} value={l}>
                {t(`sitesUi.language.${l}`)}
              </option>
            ))}
          </SelectField>
          <TextField label={t('sitesUi.field.replyToName')} hint={t('sitesUi.field.replyToHint')} value={form.replyToName} onChange={set('replyToName')} disabled={!canEdit} />
          <TextField label={t('sitesUi.field.replyToEmail')} type="email" value={form.replyToEmail} onChange={set('replyToEmail')} disabled={!canEdit} error={errs['replyToEmail']} />
        </form>
      </Card>
      <div className="flex flex-wrap gap-2">
        <LinkButton to={`/manage/sites/${site.id}/fields`}>{t('sitesUi.links.fields')}</LinkButton>
        <LinkButton to={`/manage/sites/${site.id}/notices`}>{t('sitesUi.links.notices')}</LinkButton>
      </div>
      {canEdit ? (
        <Card description={t('sitesUi.archive.hint')}>
          <div className="space-y-3">
            <ErrorLine error={status.error} />
            <ConfirmButton
              label={t('sitesUi.archive')}
              confirmLabel={t('sitesUi.archive.confirm')}
              pending={status.isPending}
              onConfirm={() => status.mutate('archived')}
            />
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function GroupsTab({ site, canEdit }: { site: Site; canEdit: boolean }) {
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['groups', 'list', 'active'], queryFn: () => api.get('/groups', { query: { status: 'active' }, schema: GroupList }) });
  const current = useQuery({ queryKey: ['sites', 'groups', site.id], queryFn: () => api.get(`/sites/${site.id}/groups`, { schema: SiteGroups }) });
  const [mode, setMode] = useState<'all' | 'selected'>('all');
  const [ids, setIds] = useState<string[]>([]);
  useEffect(() => {
    if (current.data) {
      setMode(current.data.mode);
      setIds(current.data.groupIds);
    }
  }, [current.data]);
  const save = useMutation({
    mutationFn: () => api.put(`/sites/${site.id}/groups`, { body: { mode, groupIds: ids }, schema: SiteGroups }),
    onSuccess: async (r) => {
      qc.setQueryData(['sites', 'groups', site.id], r);
      await qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  if (groups.isError) return <ErrorPanel error={groups.error} onRetry={() => void groups.refetch()} />;
  if (current.isError) return <ErrorPanel error={current.error} onRetry={() => void current.refetch()} />;
  if (!groups.data || !current.data) return <Spinner />;
  const changed = mode !== current.data.mode || (mode === 'selected' && [...ids].sort().join() !== [...current.data.groupIds].sort().join());
  return (
    <Card
      title={t('sitesUi.groups.title')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        <ErrorLine error={save.error} />
        {groups.data.length === 0 ? (
          <EmptyState message={t('sitesUi.groups.none')} />
        ) : (
          <>
            <fieldset className="space-y-2" disabled={!canEdit}>
              <legend className="sr-only">{t('sitesUi.groups.title')}</legend>
              {(['all', 'selected'] as const).map((m) => (
                <label key={m} className="flex items-center gap-3 text-sm">
                  <input type="radio" name="group-mode" className="h-5 w-5 accent-[var(--brand-primary)]" checked={mode === m} onChange={() => setMode(m)} />
                  {t(`sitesUi.groups.${m}`)}
                </label>
              ))}
            </fieldset>
            {mode === 'selected' ? (
              <>
                <Alert kind="info">{t('sitesUi.groups.warning')}</Alert>
                <ul aria-label={t('sitesUi.groups.list')} className="space-y-2">
                  {groups.data.map((g) => (
                    <li key={g.id}>
                      <CheckboxField
                        label={g.name}
                        disabled={!canEdit}
                        checked={ids.includes(g.id)}
                        onChange={(e) => setIds((l) => (e.target.checked ? [...l, g.id] : l.filter((x) => x !== g.id)))}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}

type SwitchField = readonly [key: string, label: string, hint?: string];

const PRIVACY_FIELDS: readonly SwitchField[] = [
  ['autocompleteReturningVisitors', 'sitesUi.privacy.autocomplete'],
  ['exactMatchPreRegisteredOnly', 'sitesUi.privacy.exactMatch'],
  ['hideVisitorsOnSignOutList', 'sitesUi.privacy.hideSignOutList'],
  ['requireFullName', 'sitesUi.privacy.requireFullName'],
  ['hideProductLogoOnThankYou', 'sitesUi.privacy.hideProductLogo'],
];

const FEATURE_FIELDS: readonly SwitchField[] = [
  ['qrScanning', 'sitesUi.features.qr'],
  ['visitorPhotos', 'sitesUi.features.photos', 'sitesUi.features.photosHint'],
];

/** A card of on/off switches that saves one section of the site's `sites` settings. */
function SwitchesTab({
  siteId,
  name,
  title,
  fields,
  canEdit,
}: {
  siteId: string;
  name: 'privacy' | 'features';
  title: string;
  fields: readonly SwitchField[];
  canEdit: boolean;
}) {
  const { query, save, values } = useScopedSettings(`/sites/${siteId}/settings`, 'sites');
  const stored = section<Record<string, boolean>>(values, name);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  useEffect(() => setDraft({}), [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const merged = { ...stored, ...draft };
  const changed = Object.keys(draft).some((k) => draft[k] !== stored[k]);
  return (
    <Card
      title={title}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ [name]: merged })}>
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        <ErrorLine error={save.error} />
        {fields.map(([key, label, hint]) => (
          <CheckboxField
            key={key}
            label={t(label)}
            hint={hint ? t(hint) : undefined}
            disabled={!canEdit}
            checked={Boolean(merged[key])}
            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))}
          />
        ))}
      </div>
    </Card>
  );
}

export function SiteDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const canManage = useCan('sites.manage');
  const q = useQuery({ queryKey: ['sites', 'detail', id], queryFn: () => api.get(`/sites/${id}`, { schema: Site }) });
  const tabs = [
    { key: 'details', label: t('sitesUi.tab.details') },
    { key: 'groups', label: t('sitesUi.tab.groups') },
    { key: 'look', label: t('sitesUi.tab.look') },
    { key: 'privacy', label: t('sitesUi.tab.privacy') },
    { key: 'features', label: t('sitesUi.tab.features') },
    { key: 'devices', label: t('sitesUi.tab.devices') },
    { key: 'settings', label: t('sitesUi.tab.settings') },
  ];
  const [tab, setTab] = useTab(tabs);
  const restore = useMutation({
    mutationFn: () => api.patch(`/sites/${id}`, { body: { status: 'active' }, schema: Site }),
    onSuccess: async (s) => {
      qc.setQueryData(['sites', 'detail', id], s);
      await qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const site = q.data;
  const canEdit = canManage && site.status === 'active';
  return (
    <>
      <p className="mb-2 text-sm">
        <Link className="text-brand hover:underline" to="/manage/sites">
          {t('sitesUi.back')}
        </Link>
      </p>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {site.name}
            {site.status === 'archived' ? <Badge>{t('sitesUi.status.archived')}</Badge> : null}
          </span>
        }
        actions={
          canManage && site.status === 'archived' ? (
            <Button variant="primary" disabled={restore.isPending} onClick={() => restore.mutate()}>
              {t('sitesUi.restore')}
            </Button>
          ) : null
        }
      />
      {site.status === 'archived' ? (
        <div className="mb-4">
          <Alert kind="info">{t('sitesUi.archivedNotice')}</Alert>
        </div>
      ) : null}
      <ErrorLine error={restore.error} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} label={t('sitesUi.tabs')}>
        {tab === 'details' ? <DetailsTab site={site} canEdit={canEdit} /> : null}
        {tab === 'groups' ? <GroupsTab site={site} canEdit={canEdit} /> : null}
        {tab === 'look' ? <KioskLookTab site={site} canEdit={canEdit} /> : null}
        {tab === 'privacy' ? <SwitchesTab siteId={id} name="privacy" title={t('sitesUi.privacy.title')} fields={PRIVACY_FIELDS} canEdit={canEdit} /> : null}
        {tab === 'features' ? <SwitchesTab siteId={id} name="features" title={t('sitesUi.features.title')} fields={FEATURE_FIELDS} canEdit={canEdit} /> : null}
        {tab === 'devices' ? (
          <Card title={t('sitesUi.devices.title')} description={t('sitesUi.devices.description')}>
            <LinkButton to={`/manage/devices?siteId=${site.id}`}>{t('sitesUi.devices.open')}</LinkButton>
          </Card>
        ) : null}
        {tab === 'settings' ? <SettingsCards scope="sites" scopeId={id} canEdit={canEdit} exclude={['sites']} /> : null}
      </Tabs>
    </>
  );
}
